// frontend/src/hooks/useAuth.js
// Matches HR system pattern: reads localStorage on mount, exposes login/logout
// Namespaced keys (legal_token, legal_user) so it doesn't clash with HR system

import { useState, useEffect } from 'react';

// Storage key constants — centralised so a typo can't cause a silent bug
const TOKEN_KEY = 'legal_token';
const USER_KEY  = 'legal_user';

export function useAuth() {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token      = localStorage.getItem(TOKEN_KEY);
    const storedUser = localStorage.getItem(USER_KEY);

    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('[useAuth] Failed to parse stored user:', error);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
    }

    setLoading(false);
  }, []);

  // Called after successful login — stores tokens and user data
  const login = (accessToken, userData) => {
    // Normalize: ensure userId is always available regardless of API response shape
    const normalized = { ...userData, userId: userData.id ?? userData.userId };
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(normalized));
    setUser(normalized);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  // ─── Derived helpers ──────────────────────────────────────────────────────────

  const isAdmin   = user?.userType === 'admin';
  const isMaster  = user?.userType === 'admin' && user?.role === 'MASTER';
  const isVendor  = user?.userType === 'vendor';
  const isPartner = user?.userType === 'partner';

  // Check if this admin can access a given entityCacheId
  // Used on the frontend to hide/show scoped content without extra API calls
  const canAccessEntity = (entityId) => {
    if (!isAdmin) return false;
    if (isMaster || user?.scopeType === 'GLOBAL') return true;
    return Array.isArray(user?.scopeEntityIds) && user.scopeEntityIds.includes(entityId);
  };

  return {
    user,
    loading,
    login,
    logout,
    isAdmin,
    isMaster,
    isVendor,
    isPartner,
    canAccessEntity,
  };
}
